import { useMemo, useState } from "react";
import {
  Server,
  Plus,
  Pencil,
  Trash2,
  Search,
  ShieldCheck,
  Wrench,
  Activity,
  FileCheck2,
  Layers,
  X,
  Save,
} from "lucide-react";
import { CenterHeader } from "./center/CenterPrimitives";
import { TEMPLATES, INSTANCES, type Template, type Instance, type MachineStatus } from "@/lib/machines-data";

type TabId = "templates" | "instances" | "calibration" | "autotest" | "policies";

const TABS: { id: TabId; label: string; icon: typeof Server }[] = [
  { id: "templates", label: "Templates", icon: Layers },
  { id: "instances", label: "Instances", icon: Server },
  { id: "calibration", label: "Calibration", icon: Wrench },
  { id: "autotest", label: "Auto-test", icon: Activity },
  { id: "policies", label: "Policies", icon: ShieldCheck },
];

function daysUntil(date: string) {
  const diff = Math.round((new Date(date).getTime() - Date.now()) / 86400000);
  return diff;
}

function StatusBadge({ status }: { status: MachineStatus }) {
  const cls =
    status === "Actif"
      ? "bg-success/20 text-success ring-success/30"
      : status === "Hors ligne"
        ? "bg-white/5 text-muted-foreground ring-white/10"
        : status === "Maintenance"
          ? "bg-[oklch(0.82_0.16_85)]/15 text-[oklch(0.85_0.15_85)] ring-[oklch(0.82_0.16_85)]/30"
          : "bg-accent/15 text-accent ring-accent/30";
  return <span className={`rounded-md px-2 py-0.5 text-[10px] font-medium ring-1 ${cls}`}>{status}</span>;
}

function Chip({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "success" }) {
  const cls = tone === "success" ? "bg-success/15 text-success ring-success/30" : "bg-white/5 text-muted-foreground ring-white/10";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${cls}`}>{children}</span>;
}

function KpiBlock({ label, value, hint, tone = "neutral" }: { label: string; value: string; hint?: string; tone?: "neutral" | "success" | "warning" | "danger" }) {
  const valueTone =
    tone === "danger" ? "text-accent" : tone === "warning" ? "text-[oklch(0.85_0.15_85)]" : tone === "success" ? "text-success" : "text-foreground";
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-3 text-4xl font-light tracking-tight ${valueTone}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function MachinesView({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<TabId>("templates");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string>(TEMPLATES[0].id);
  const [editing, setEditing] = useState<Template | null>(null);

  const upcoming = useMemo(
    () =>
      [...TEMPLATES]
        .sort((a, b) => new Date(a.calibration.next).getTime() - new Date(b.calibration.next).getTime())
        .slice(0, 3),
    [],
  );
  const overdue = TEMPLATES.filter((t) => daysUntil(t.calibration.next) < 0).length;

  const filteredTemplates = TEMPLATES.filter(
    (t) => !query || `${t.name} ${t.vendor} ${t.model} ${t.scope}`.toLowerCase().includes(query.toLowerCase()),
  );
  const filteredInstances = INSTANCES.filter(
    (i) => !query || `${i.name} ${i.host} ${i.protocol}`.toLowerCase().includes(query.toLowerCase()),
  );

  const selected = TEMPLATES.find((t) => t.id === selectedId) ?? TEMPLATES[0];

  return (
    <div className="flex flex-col gap-5">
      <CenterHeader title="Configuration machines" scope={`${TEMPLATES.length} templates · ${INSTANCES.length} machines déployées · ${overdue} calibration${overdue > 1 ? "s" : ""} en retard`} onBack={onBack} />

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        <KpiBlock label="Templates" value={String(TEMPLATES.length)} hint="configurations réutilisables" />
        <KpiBlock label="Instances déployées" value={String(INSTANCES.length)} hint="machines actives" tone="success" />
        <KpiBlock label="Calibrations en retard" value={String(overdue)} hint="à planifier" tone={overdue > 0 ? "danger" : "neutral"} />
        <div className="card-surface rounded-2xl p-5">
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Prochaines calibrations</div>
          <ul className="mt-3 space-y-1.5">
            {upcoming.map((t) => (
              <li key={t.id} className="flex items-center justify-between text-xs">
                <span className="font-mono text-foreground">{t.calibration.next}</span>
                <span className="text-muted-foreground">({t.vendor})</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="card-surface inline-flex items-center gap-1 rounded-full p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            const count = t.id === "templates" ? TEMPLATES.length : t.id === "instances" ? INSTANCES.length : null;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={
                  "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all " +
                  (isActive
                    ? "bg-success/20 text-success ring-1 ring-success/40 shadow-[0_8px_24px_-12px_oklch(0.78_0.18_150/0.6)]"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5")
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {count !== null && <span className="opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className="card-surface h-9 w-64 rounded-full pl-8 pr-3 text-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-success/40"
            />
          </div>
          <button className="inline-flex items-center gap-2 rounded-full bg-success px-3.5 py-1.5 text-xs font-medium text-success-foreground hover:bg-success/90">
            <Plus className="h-3.5 w-3.5" />
            {tab === "instances" ? "Nouvelle instance" : "Nouveau template"}
          </button>
        </div>
      </div>

      {tab === "templates" && (
        <div className="text-xs text-muted-foreground">Chaque template encapsule : driver · policies · calibration · auto-test · mapping</div>
      )}
      {tab === "instances" && (
        <div className="text-xs text-muted-foreground">Toutes les machines physiques connectées · héritent des policies du template assigné</div>
      )}

      {/* Master + detail */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_1fr]">
        {/* MASTER */}
        <div className="space-y-2.5">
          {tab === "templates" &&
            filteredTemplates.map((t) => {
              const active = t.id === selectedId;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className={
                    "card-surface group flex w-full items-start gap-3 rounded-2xl p-4 text-left transition-all " +
                    (active ? "ring-1 ring-success/50 shadow-[0_8px_24px_-12px_oklch(0.78_0.18_150/0.5)]" : "hover:ring-1 hover:ring-white/10")
                  }
                >
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-success/15 text-success">
                    <Server className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-sm font-medium">{t.name}</div>
                      <Chip>{t.classification}</Chip>
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{t.description}</div>
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                      {t.driver} · {t.host}:{t.port}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <Chip>{t.installs} installés</Chip>
                      {t.tags.map((tag) => (
                        <Chip key={tag}>{tag}</Chip>
                      ))}
                      <Chip tone="success">auto-test {t.autoTest}</Chip>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 opacity-60 group-hover:opacity-100">
                    <span
                      role="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(t.id);
                        setEditing(t);
                      }}
                      className="grid h-7 w-7 cursor-pointer place-items-center rounded-md bg-white/5 hover:bg-white/10"
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </span>
                    <span className="grid h-7 w-7 cursor-pointer place-items-center rounded-md bg-white/5 hover:bg-white/10">
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </span>
                  </div>
                </button>
              );
            })}

          {tab === "instances" &&
            filteredInstances.map((i) => (
              <div key={i.id} className="card-surface flex items-center gap-3 rounded-2xl p-4">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/5 text-muted-foreground">
                  <Server className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-medium">{i.name}</div>
                    <StatusBadge status={i.status} />
                    {i.templateId && <Chip>Template: {i.templateId}</Chip>}
                  </div>
                  <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                    {i.host}:{i.port} · FW {i.firmware} · {i.protocol}
                  </div>
                </div>
                <div className="shrink-0 text-right text-[11px]">
                  <div className="text-muted-foreground">Prochaine calibration</div>
                  <div
                    className={
                      "font-mono " +
                      (!i.nextCalibration
                        ? "text-[oklch(0.85_0.15_85)]"
                        : daysUntil(i.nextCalibration) < 0
                          ? "text-accent"
                          : "text-success")
                    }
                  >
                    {i.nextCalibration ? `${i.nextCalibration} (${daysUntil(i.nextCalibration)}j)` : "non assigné"}
                  </div>
                </div>
                <button className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white/5 hover:bg-white/10">
                  <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </div>
            ))}

          {tab === "calibration" && (
            <div className="card-surface rounded-2xl p-6 text-sm text-muted-foreground">
              Calendrier de calibration par template — {TEMPLATES.length} planifications actives.
              <ul className="mt-4 space-y-2 text-xs">
                {TEMPLATES.map((t) => (
                  <li key={t.id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <span className="text-foreground">{t.name}</span>
                    <span className="font-mono">{t.calibration.next} · tous les {t.calibration.intervalDays}j</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === "autotest" && (
            <div className="card-surface rounded-2xl p-6 text-sm text-muted-foreground">
              Scénarios auto-test par template
              <ul className="mt-4 space-y-2 text-xs">
                {TEMPLATES.map((t) => (
                  <li key={t.id} className="rounded-lg bg-white/[0.03] px-3 py-2">
                    <div className="text-foreground">{t.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {t.scenarios.map((s) => (
                        <Chip key={s} tone="success">{s}</Chip>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === "policies" && (
            <div className="card-surface rounded-2xl p-6 text-sm text-muted-foreground">
              Policies de sécurité héritées par les instances
              <ul className="mt-4 space-y-2 text-xs">
                {TEMPLATES.map((t) => (
                  <li key={t.id} className="rounded-lg bg-white/[0.03] px-3 py-2">
                    <div className="text-foreground">{t.name}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {t.policies.tls && <Chip tone="success">TLS</Chip>}
                      {t.policies.mtls && <Chip tone="success">mTLS</Chip>}
                      {t.policies.ipWhitelist && <Chip tone="success">IP Whitelist</Chip>}
                      {t.policies.pseudo && <Chip tone="success">Pseudonymisation</Chip>}
                      {t.policies.audit && <Chip tone="success">Audit</Chip>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* DETAIL */}
        <aside className="card-surface sticky top-4 h-fit rounded-2xl p-5">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Template sélectionné</div>
              <div className="mt-1 truncate text-sm font-medium">{selected.name}</div>
            </div>
            <Chip>{selected.classification}</Chip>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Vendor</div>
              <div className="mt-0.5">{selected.vendor}</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Model</div>
              <div className="mt-0.5">{selected.model}</div>
            </div>
            <div className="col-span-2 rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Driver</div>
              <div className="mt-0.5 font-mono text-[11px]">{selected.driver}</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Host</div>
              <div className="mt-0.5 font-mono">{selected.host}</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Port</div>
              <div className="mt-0.5 font-mono">{selected.port}</div>
            </div>
          </div>

          <div className="mt-4 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Policies</div>
          <ul className="mt-2 space-y-1.5 text-xs">
            {[
              ["TLS obligatoire", selected.policies.tls],
              ["Mutual TLS (mTLS)", selected.policies.mtls],
              ["IP Whitelist", selected.policies.ipWhitelist],
              ["Pseudonymisation en transit", selected.policies.pseudo],
              ["Audit de chaque message", selected.policies.audit],
            ].map(([label, on]) => (
              <li key={label as string} className="flex items-center gap-2">
                <FileCheck2 className={"h-3.5 w-3.5 " + (on ? "text-success" : "text-muted-foreground/40")} />
                <span className={on ? "" : "text-muted-foreground line-through"}>{label}</span>
              </li>
            ))}
          </ul>

          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Intervalle</div>
              <div className="mt-0.5">{selected.calibration.intervalDays} jours</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2.5 ring-1 ring-white/5">
              <div className="text-[10px] uppercase text-muted-foreground">Prochaine</div>
              <div className="mt-0.5 font-mono">{selected.calibration.next}</div>
            </div>
          </div>

          <div className="mt-4 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Scénarios auto-test</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {selected.scenarios.map((s) => (
              <Chip key={s} tone="success">{s}</Chip>
            ))}
          </div>

          <button
            onClick={() => setEditing(selected)}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-success px-3.5 py-2 text-xs font-medium text-success-foreground hover:bg-success/90"
          >
            <Pencil className="h-3.5 w-3.5" />
            Éditer ce template
          </button>
        </aside>
      </div>

      {/* Edit drawer */}
      {editing && <EditDrawer template={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <input
        defaultValue={value}
        className="card-surface h-9 rounded-md px-3 text-xs outline-none focus:ring-1 focus:ring-success/40"
      />
    </label>
  );
}

function EditDrawer({ template, onClose }: { template: Template; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm">
      <div className="flex w-full max-w-xl flex-col overflow-y-auto bg-card p-6 ring-1 ring-white/10">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Édition template</div>
            <div className="text-base font-semibold">{template.name}</div>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-md bg-white/5 hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Field label="Nom du template" value={template.name} />
          <Field label="Driver" value={template.driver} />
          <Field label="Vendor" value={template.vendor} />
          <Field label="Model" value={template.model} />
          <Field label="Host par défaut" value={template.host} />
          <Field label="Port" value={template.port} />
        </div>

        <label className="mt-3 flex flex-col gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Description</span>
          <textarea
            defaultValue={template.description}
            className="card-surface min-h-[72px] rounded-md px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-success/40"
          />
        </label>

        <div className="mt-5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Policies</div>
        <ul className="mt-2 space-y-1.5 text-xs">
          {[
            ["TLS obligatoire", template.policies.tls],
            ["Mutual TLS (mTLS)", template.policies.mtls],
            ["IP Whitelist", template.policies.ipWhitelist],
            ["Pseudonymisation en transit", template.policies.pseudo],
            ["Audit de chaque message", template.policies.audit],
          ].map(([label, on]) => (
            <li key={label as string} className="flex items-center gap-2">
              <input type="checkbox" defaultChecked={!!on} className="accent-success" />
              <span>{label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Field label="Intervalle (jours)" value={template.calibration.intervalDays} />
          <Field label="Prochaine" value={template.calibration.next} />
        </div>

        <div className="mt-5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Auto-test</div>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <input type="checkbox" defaultChecked={template.autoTest !== "off"} className="accent-success" />
          Activé
        </div>

        <div className="mt-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Scénarios</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {template.scenarios.map((s) => (
            <Chip key={s} tone="success">{s}</Chip>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-end gap-2 pt-6">
          <button onClick={onClose} className="rounded-full bg-white/5 px-4 py-2 text-xs hover:bg-white/10">
            Annuler
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full bg-success px-4 py-2 text-xs font-medium text-success-foreground hover:bg-success/90"
          >
            <Save className="h-3.5 w-3.5" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
